import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RequestCallButton } from "@/components/RequestCall";
import { toast } from "sonner";
import {
  AlertCircle, ArrowRight, Bookmark, CalendarDays, CheckCircle2,
  ChevronRight, Clock, ExternalLink, FileText, GitCompare, Globe2, Info,
  MapPin, Send, ShieldCheck, Sparkles, Wallet, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import { Footer } from "@/components/site/Footer";
import { Navbar } from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import { compactList, formatDate, levelLabel, providerInitials, statusLabel, typeLabel } from "@/lib/opportunities";

const Section = ({ id, title, kicker, children, testId }) => (
  <section id={id} data-testid={testId} className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgba(10,37,64,0.04)]">
    {kicker && <p className="text-xs font-bold uppercase tracking-[0.18em] text-cobalt">{kicker}</p>}
    <h2 className="mt-1 font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">{title}</h2>
    <div className="mt-5">{children}</div>
  </section>
);

const Fact = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slatey">
      <Icon className="h-4 w-4 text-cobalt" /> {label}
    </div>
    <p className="mt-2 text-sm sm:text-base font-bold text-ink">{value || "Not specified"}</p>
  </div>
);

const ProviderMark = ({ provider, large = false }) => {
  const size = large ? "h-16 w-16" : "h-12 w-12";
  if (provider?.logo) {
    return <img src={provider.logo} alt="" className={`${size} rounded-2xl border border-slate-200 object-cover`} />;
  }
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-2xl bg-cobalt/10 font-display font-bold text-cobalt`}>
      {providerInitials(provider?.name)}
    </div>
  );
};

const ActionButtons = ({ opp, busy, onSave, onCompare, onEnquire, onApply, prefix }) => {
  const saved = Boolean(opp.learner_state?.saved);
  const compared = Boolean(opp.learner_state?.in_compare);
  const existingApplication = opp.learner_state?.existing_application;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button data-testid={`${prefix}-save-button`} onClick={onSave} disabled={busy}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all ${saved ? "bg-cobalt text-white" : "border border-slate-300 bg-white text-ink hover:border-cobalt hover:text-cobalt"}`}>
        <Bookmark className={`h-4 w-4 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved" : "Save"}
      </button>
      <button data-testid={`${prefix}-compare-button`} onClick={onCompare} disabled={busy}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all ${compared ? "bg-ink text-white" : "border border-slate-300 bg-white text-ink hover:border-cobalt hover:text-cobalt"}`}>
        <GitCompare className="h-4 w-4" /> {compared ? "In compare" : "Compare"}
      </button>
      <button data-testid={`${prefix}-enquire-button`} onClick={onEnquire} disabled={busy}
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-ink transition-colors hover:border-cobalt hover:text-cobalt">
        <Send className="h-4 w-4" /> Enquire
      </button>
      <RequestCallButton opportunitySlug={opp.slug} sourcePage="opportunity" testId={`${prefix}-request-call`}
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-ink transition-colors hover:border-cobalt hover:text-cobalt" />
      <button data-testid={`${prefix}-apply-button`} onClick={onApply} disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-cobalt px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_24px_rgba(23,147,90,0.22)] transition-transform hover:scale-[1.02] active:scale-95">
        {existingApplication ? "View application" : "Apply"} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, testId }) => (
  <div data-testid={testId} className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true">
    <button aria-label="Close dialog" className="absolute inset-0 cursor-default" onClick={onClose} />
    <div className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">{title}</h2>
        <button data-testid={`${testId}-close`} onClick={onClose} className="rounded-full p-2 text-slatey hover:bg-surface hover:text-ink" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

const RequirementCard = ({ requirement }) => {
  const categoryLabel = { academic: "Academic", english: "English language", other: "Provider requirement" }[requirement.category] || "Requirement";
  const rows = [
    ["Qualification", requirement.qualification],
    ["Level", requirement.qualification_level],
    ["Required subjects", compactList(requirement.required_subjects, "")],
    ["Minimum grade", requirement.minimum_grade],
    ["Equivalents", compactList(requirement.equivalent_qualifications, "")],
    ["Relevant experience", requirement.relevant_experience || requirement.work_experience],
    ["Test", requirement.test_type],
    ["Minimum score", requirement.minimum_score],
    ["Accepted alternatives", compactList(requirement.accepted_alternatives, "")],
    ["Exemptions / notes", requirement.exemptions_notes || requirement.notes],
    ["Portfolio", requirement.portfolio],
    ["Interview", requirement.interview],
    ["Professional registration", requirement.professional_registration],
    ["Background check", requirement.background_check],
  ].filter(([, value]) => value);
  return (
    <article className="rounded-2xl border border-slate-200 bg-surface p-5" data-testid={`requirement-${requirement.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-bold text-ink">
          {requirement.qualification || requirement.test_type || categoryLabel}
        </h3>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slatey">{categoryLabel}</span>
      </div>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs font-bold uppercase tracking-[0.12em] text-slatey">{label}</dt>
            <dd className="mt-1 text-sm font-medium text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
};

const BulletList = ({ items, empty = "Information will be confirmed by the provider." }) => {
  if (!items?.length) return <p className="text-sm text-slatey">{empty}</p>;
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-sm leading-6 text-slatey">
          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-cobalt" /> <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const intakeIsAvailable = (intake) => {
  if (!intake || !["open", "published", "closing_soon"].includes(intake.status)) return false;
  if (!intake.application_closing_date) return true;
  return new Date(`${intake.application_closing_date}T23:59:59`) >= new Date();
};

export default function OpportunityDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [selectedIntakeId, setSelectedIntakeId] = useState("");
  const [startingApplication, setStartingApplication] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.querySelectorAll('[data-server-opportunity-seo="true"]').forEach((element) => element.remove());
  }, []);

  useEffect(() => {
    setOpp(null);
    setError("");
    api.get(`/opportunities/${slug}`)
      .then((response) => setOpp(response.data))
      .catch((requestError) => setError(formatApiErrorDetail(requestError.response?.data?.detail)));
  }, [slug]);

  const requireLearner = () => {
    if (!user) {
      navigate("/app/login");
      return false;
    }
    if (user.role !== "individual") {
      toast.error("Learner actions are available from an individual account.");
      return false;
    }
    return true;
  };

  const updateLearnerState = (patch) => {
    setOpp((current) => ({ ...current, learner_state: { ...(current?.learner_state || {}), ...patch } }));
  };

  const toggleSave = async () => {
    if (!requireLearner()) return;
    setBusy(true);
    try {
      const saved = Boolean(opp.learner_state?.saved);
      const response = saved
        ? await api.delete(`/individual/opportunities/${slug}/save`)
        : await api.post(`/individual/opportunities/${slug}/save`);
      updateLearnerState({ saved: response.data.saved });
      toast.success(response.data.saved ? "Opportunity saved." : "Opportunity removed from saved list.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleCompare = async () => {
    if (!requireLearner()) return;
    setBusy(true);
    try {
      const compared = Boolean(opp.learner_state?.in_compare);
      const response = compared
        ? await api.delete(`/individual/opportunities/${slug}/compare`)
        : await api.post(`/individual/opportunities/${slug}/compare`);
      updateLearnerState({ in_compare: response.data.in_compare });
      toast.success(response.data.in_compare ? "Added to comparison." : "Removed from comparison.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEnquiry = () => {
    if (requireLearner()) setEnquiryOpen(true);
  };

  const openApply = () => {
    if (!requireLearner()) return;
    const existingApplication = opp.learner_state?.existing_application;
    if (existingApplication) {
      navigate(`/app/applications/${existingApplication.id || existingApplication.slug}`);
      return;
    }
    // Honour an intake chosen earlier in the Navigator journey (?intake=<id>)
    const preferredId = searchParams.get("intake");
    const preferred = (opp.intakes || []).find((intake) => intake.id === preferredId && intakeIsAvailable(intake));
    const firstAvailable = preferred || (opp.intakes || []).find(intakeIsAvailable);
    setSelectedIntakeId(firstAvailable?.id || "");
    setApplyOpen(true);
  };

  const startApplication = async () => {
    if (!selectedIntakeId) {
      toast.error("Select an available intake to continue.");
      return;
    }
    setStartingApplication(true);
    try {
      const response = await api.post("/individual/applications", {
        opportunity_slug: opp.slug,
        intake_id: selectedIntakeId,
      });
      const application = response.data.application?.application;
      toast.success(response.data.reopened ? "Draft application reopened." : "Draft application started.");
      setApplyOpen(false);
      navigate(`/app/applications/${application.id}`);
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setStartingApplication(false);
    }
  };

  const submitEnquiry = async () => {
    if (message.trim().length < 10) {
      toast.error("Please enter at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post(`/individual/opportunities/${slug}/enquiry`, { message: message.trim() });
      toast.success("Your enquiry has been sent.");
      setMessage("");
      setEnquiryOpen(false);
      if (!response.data.email_sent) toast.info("The enquiry is saved; email notification is temporarily unavailable.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-surface">
        <Navbar />
        <main className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-5 pt-24 text-center">
          <AlertCircle className="h-10 w-10 text-cobalt" />
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-ink">Opportunity unavailable</h1>
          <p className="mt-3 text-slatey">This opportunity may be draft, archived or no longer available.</p>
          <Link to="/app/discover" className="mt-6 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Explore opportunities</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!opp) {
    return (
      <div className="min-h-screen bg-surface">
        <Navbar />
        <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
          <div className="h-6 w-48 animate-pulse rounded bg-slate-200" />
          <div className="mt-5 h-12 max-w-2xl animate-pulse rounded bg-slate-200" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((n) => <div key={n} className="h-28 animate-pulse rounded-3xl bg-white" />)}
          </div>
        </main>
      </div>
    );
  }

  const provider = opp.provider || {};
  const description = opp.description || {};
  const fees = opp.fees_detail || {};
  const intl = opp.international_requirements || {};
  const application = opp.application_requirements || {};
  const actionProps = { opp, busy, onSave: toggleSave, onCompare: toggleCompare, onEnquire: openEnquiry, onApply: openApply };

  return (
    <div data-testid="opportunity-details-page" className="min-h-screen bg-surface pb-24 lg:pb-0">
      <Seo
        title={opp.seo?.title || `${opp.title} | OpenDegree`}
        description={opp.seo?.description || description.overview || "Education opportunity on OpenDegree."}
        path={`/opportunities/${opp.slug}`}
        type="article"
        jsonLd={opp.seo?.json_ld}
        noindex={opp.visibility !== "public"}
        image={opp.seo?.og?.image}
      />
      <Navbar />

      <main className="pt-[72px]">
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 lg:py-12">
            <nav aria-label="Breadcrumb" data-testid="opportunity-breadcrumbs" className="flex flex-wrap items-center gap-1.5 text-sm text-slatey">
              <Link to="/" className="hover:text-cobalt">Home</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="font-semibold text-ink">{opp.title}</span>
            </nav>

            <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px] lg:items-start">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-cobalt/10 px-3 py-1 text-xs font-bold text-cobalt">{typeLabel(opp.opportunity_type)}</span>
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold capitalize text-slatey">{statusLabel(opp.status)}</span>
                  {opp.visibility === "unlisted" && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">Unlisted · noindex</span>}
                </div>
                {opp.match != null && (
                  <div data-testid="opportunity-match" className="mt-5 inline-flex items-center gap-2 rounded-full bg-cobalt px-4 py-2 text-sm font-bold text-white">
                    <Sparkles className="h-4 w-4" /> {opp.match}% match
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Link to={`/eligibility/${opp.slug}`} data-testid="opportunity-check-eligibility" className="inline-flex items-center gap-2 rounded-full border border-cobalt bg-white px-4 py-2 text-sm font-bold text-cobalt hover:bg-cobalt/5">
                    Check eligibility <ArrowRight className="h-4 w-4" />
                  </Link>
                  <span className="text-xs font-semibold text-slatey">Match is not confirmed eligibility.</span>
                </div>
                <h1 data-testid="opportunity-title" className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl lg:text-6xl">
                  {opp.title}
                </h1>
                <div className="mt-4 flex items-center gap-3">
                  <ProviderMark provider={provider} />
                  <div>
                    <p data-testid="opportunity-provider-name" className="font-display text-xl font-bold text-ink">{provider.name}</p>
                    <p className="text-sm text-slatey">{provider.provider_type}</p>
                  </div>
                </div>
                <p className="mt-5 max-w-3xl text-base leading-8 text-slatey sm:text-lg">{description.overview}</p>
                <div className="mt-7 hidden lg:block"><ActionButtons {...actionProps} prefix="opportunity" /></div>
              </div>

              <aside data-testid="provider-card" className="rounded-3xl border border-slate-200 bg-surface p-6">
                <div className="flex items-start gap-4">
                  <ProviderMark provider={provider} large />
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-cobalt">Education provider</p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-ink">{provider.name}</h2>
                    <p className="mt-1 text-sm text-slatey">{provider.provider_type}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-6 text-slatey">{provider.description}</p>
                <div className="mt-4 space-y-2 text-sm text-slatey">
                  <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-cobalt" /> {[provider.location?.city, provider.location?.country].filter(Boolean).join(", ") || provider.country}</p>
                  <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cobalt" /> Provider status: <span className="font-semibold capitalize text-ink">{provider.verification_status || provider.status}</span></p>
                </div>
                {provider.website && (
                  <a data-testid="provider-website-link" href={provider.website} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-cobalt hover:text-cobalt-hover">
                    Provider website <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <div className="mt-5 rounded-2xl border border-cobalt/15 bg-white p-4 text-xs leading-5 text-slatey">
                  <Info className="mb-2 h-4 w-4 text-cobalt" />
                  OpenDegree supports discovery, matching and application journeys. {provider.name} retains the final admissions decision.
                </div>
              </aside>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 lg:py-10">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Fact icon={FileText} label="Qualification" value={opp.qualification} testId="fact-qualification" />
            <Fact icon={MapPin} label="Location" value={opp.location} testId="fact-location" />
            <Fact icon={Clock} label="Duration" value={opp.duration} testId="fact-duration" />
            <Fact icon={Wallet} label="Fees" value={opp.fees} testId="fact-fees" />
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_380px]">
            <div className="space-y-6">
              <Section id="overview" title="What you will study" kicker="Programme content" testId="study-section">
                <BulletList items={description.what_you_will_study} />
                {description.modules?.length > 0 && (
                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {description.modules.map((module) => (
                      <article key={module.title} className="rounded-2xl border border-slate-200 bg-surface p-4" data-testid={`module-${module.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-display text-lg font-bold text-ink">{module.title}</h3>
                          {module.credits && <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-cobalt">{module.credits} credits</span>}
                        </div>
                        <p className="mt-2 text-sm leading-6 text-slatey">{module.description}</p>
                      </article>
                    ))}
                  </div>
                )}
              </Section>

              <Section id="outcomes" title="Learning and career outcomes" kicker="Outcomes" testId="outcomes-section">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slatey">Learning outcomes</h3>
                    <div className="mt-3"><BulletList items={description.learning_outcomes} /></div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slatey">Career progression</h3>
                    <div className="mt-3"><BulletList items={description.career_outcomes} /></div>
                  </div>
                </div>
                {description.assessment_approach && (
                  <div className="mt-6 rounded-2xl bg-surface p-4">
                    <h3 className="font-bold text-ink">Assessment approach</h3>
                    <p className="mt-2 text-sm leading-6 text-slatey">{description.assessment_approach}</p>
                  </div>
                )}
              </Section>

              <Section id="requirements" title="Requirements" kicker="Structured requirements" testId="requirements-section">
                <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
                  <strong>Match is not eligibility.</strong> Requirements below are structured for transparency, but OpenDegree has not confirmed your eligibility yet.
                </div>
                <div className="space-y-4">
                  {opp.requirements?.map((requirement) => <RequirementCard key={requirement.id} requirement={requirement} />)}
                </div>
              </Section>

              <Section id="international" title="International information" kicker="International readiness" testId="international-section">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-bold text-ink">International applicants</p>
                    <p className="mt-1 text-sm text-slatey">{intl.eligible ? "This opportunity can consider international applicants." : "International availability is limited or funding/residency restricted."}</p>
                    <p className="mt-4 text-sm font-bold text-ink">Qualification recognition</p>
                    <p className="mt-1 text-sm leading-6 text-slatey">{intl.qualification_recognition || "Provider will confirm recognition."}</p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-ink">International fees</p>
                    <p className="mt-1 text-sm text-slatey">{intl.international_fees || "Not specified"}</p>
                    <p className="mt-4 text-sm font-bold text-ink">Additional documents</p>
                    <div className="mt-2"><BulletList items={intl.additional_documents} empty="No additional documents listed." /></div>
                  </div>
                </div>
                {intl.country_specific_requirements?.length > 0 && (
                  <div className="mt-6 rounded-2xl bg-surface p-4">
                    <h3 className="font-bold text-ink">Country-specific requirements</h3>
                    <ul className="mt-3 space-y-2">
                      {intl.country_specific_requirements.map((item) => (
                        <li key={`${item.country}-${item.requirement}`} className="text-sm text-slatey"><strong className="text-ink">{item.country}:</strong> {item.requirement}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4">
                  <h3 className="flex items-center gap-2 font-bold text-ink"><Globe2 className="h-4 w-4 text-cobalt" /> Visa and pre-arrival signposting</h3>
                  <p className="mt-2 text-sm leading-6 text-slatey">{intl.visa?.relevance}</p>
                  <p className="mt-2 text-xs leading-5 text-slatey"><strong>Important:</strong> {intl.visa?.guidance} {intl.visa?.specialist_advice}</p>
                </div>
              </Section>
            </div>

            <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
              <Section id="match" title="Your match" kicker="Existing matching" testId="match-section">
                {opp.match != null ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-cobalt text-lg font-extrabold text-white">{opp.match}%</div>
                      <p className="text-sm leading-6 text-slatey">This is your existing OpenDegree match score. It is not an eligibility decision.</p>
                    </div>
                    <div className="mt-4"><BulletList items={opp.match_reasons} empty="No match reasons available." /></div>
                  </>
                ) : (
                  <p className="text-sm leading-6 text-slatey">Sign in as a learner to see your existing match score and reasons.</p>
                )}
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <h3 className="text-sm font-bold text-ink">Eligibility status</h3>
                  <ul className="mt-3 space-y-2">
                    {opp.eligibility_foundation?.map((item) => (
                      <li key={item.requirement_id} className="flex items-center justify-between gap-3 rounded-xl bg-surface px-3 py-2 text-sm">
                        <span className="font-medium text-ink">{item.requirement}</span>
                        <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-bold text-slatey">{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>

              <Section id="fees" title="Fees and funding" kicker="Financial information" testId="fees-section">
                <dl className="space-y-3 text-sm">
                  <div><dt className="font-bold text-slatey">Tuition / total cost</dt><dd className="mt-1 font-display text-xl font-bold text-ink">{fees.tuition || opp.fees}</dd></div>
                  <div><dt className="font-bold text-slatey">Fee period</dt><dd className="mt-1 text-ink">{fees.fee_period || "Not specified"}</dd></div>
                  {fees.international_fee && <div><dt className="font-bold text-slatey">International fee</dt><dd className="mt-1 text-ink">{fees.international_fee}</dd></div>}
                </dl>
                <div className="mt-5 space-y-3">
                  {fees.scholarships?.length > 0 && <p className="text-sm text-slatey"><strong className="text-ink">Scholarships:</strong> {fees.scholarships.join(", ")}</p>}
                  {fees.bursaries?.length > 0 && <p className="text-sm text-slatey"><strong className="text-ink">Bursaries:</strong> {fees.bursaries.join(", ")}</p>}
                  {fees.loans?.length > 0 && <p className="text-sm text-slatey"><strong className="text-ink">Loans:</strong> {fees.loans.join(", ")}</p>}
                  {fees.employer_funding && <p className="text-sm text-slatey"><strong className="text-ink">Employer funding:</strong> Available</p>}
                  {fees.apprenticeship_funding && <p className="text-sm text-slatey"><strong className="text-ink">Apprenticeship funding:</strong> Available</p>}
                </div>
                {fees.notes && <p className="mt-4 rounded-2xl bg-surface p-3 text-xs leading-5 text-slatey">{fees.notes}</p>}
              </Section>

              <Section id="intakes" title="Available intakes" kicker="Start dates" testId="intakes-section">
                <div className="space-y-3">
                  {opp.intakes?.map((intake) => (
                    <article key={intake.id} className="rounded-2xl border border-slate-200 bg-surface p-4" data-testid={`intake-${intake.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-bold text-ink">{intake.intake_label}</h3>
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold capitalize text-cobalt">{statusLabel(intake.status)}</span>
                      </div>
                      <div className="mt-3 space-y-1.5 text-sm text-slatey">
                        <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-cobalt" /> Starts {formatDate(intake.start_date)}</p>
                        <p>Apply by {formatDate(intake.application_closing_date)}</p>
                        {intake.capacity && <p>Capacity: {intake.capacity}</p>}
                      </div>
                    </article>
                  ))}
                </div>
              </Section>

              <Section id="apply" title="What you need to apply" kicker="Application preparation" testId="application-requirements-section">
                <BulletList items={(application.required_documents || []).map((doc) => doc.label || doc.type)} empty="No required documents listed." />
                <div className="mt-5 space-y-2 text-sm text-slatey">
                  {application.personal_statement && <p><strong className="text-ink">Personal statement:</strong> {application.personal_statement}</p>}
                  {application.references && <p><strong className="text-ink">References:</strong> {application.references}</p>}
                  {application.interview && <p><strong className="text-ink">Interview:</strong> {application.interview}</p>}
                </div>
              </Section>
            </aside>
          </div>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur-xl lg:hidden" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <ActionButtons {...actionProps} prefix="mobile-opportunity" />
      </div>

      {enquiryOpen && (
        <Modal title="Enquire about this opportunity" onClose={() => setEnquiryOpen(false)} testId="enquiry-modal">
          <p className="text-sm leading-6 text-slatey">Your message will be linked to your learner account and this opportunity. OpenDegree will route the enquiry appropriately.</p>
          <textarea data-testid="enquiry-message-input" value={message} onChange={(event) => setMessage(event.target.value)} rows={7}
            placeholder={`Ask about ${opp.title}, entry requirements, intakes or next steps…`}
            className="mt-5 w-full rounded-2xl border border-slate-200 bg-surface p-4 text-sm leading-6 outline-none transition-colors focus:border-cobalt" />
          <button data-testid="enquiry-submit-button" onClick={submitEnquiry} disabled={busy}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-60">
            <Send className="h-4 w-4" /> Send enquiry
          </button>
        </Modal>
      )}

      {applyOpen && (
        <Modal title="Select your intake" onClose={() => setApplyOpen(false)} testId="apply-modal">
          <div data-testid="intake-selection-modal">
            <p className="text-sm leading-6 text-slatey">
              Choose the available intake for <strong className="text-ink">{opp.title}</strong>. This creates or reopens a draft application; nothing is submitted yet.
            </p>
            <div className="mt-5 space-y-3" role="radiogroup" aria-label="Available intakes">
              {(opp.intakes || []).map((intake) => {
                const available = intakeIsAvailable(intake);
                const selected = selectedIntakeId === intake.id;
                return (
                  <label key={intake.id} data-testid={`intake-option-${intake.id}`}
                    className={`block rounded-2xl border p-4 transition-colors ${available ? "cursor-pointer" : "cursor-not-allowed opacity-60"} ${selected ? "border-cobalt bg-cobalt/5 ring-2 ring-cobalt/15" : "border-slate-200 bg-white"}`}>
                    <input type="radio" name="intake" value={intake.id} checked={selected} disabled={!available}
                      onChange={() => setSelectedIntakeId(intake.id)} className="sr-only" />
                    <span className="flex items-start justify-between gap-3">
                      <span>
                        <span className="block font-display text-lg font-bold text-ink">{intake.intake_label}</span>
                        <span className="mt-1 block text-sm text-slatey">Starts {formatDate(intake.start_date)} · Apply by {formatDate(intake.application_closing_date)}</span>
                        {intake.mode && <span className="mt-1 block text-sm text-slatey">Mode: {intake.mode}</span>}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${available ? "bg-cobalt/10 text-cobalt" : "bg-slate-100 text-slatey"}`}>
                        {available ? (selected ? "Selected" : "Available") : "Unavailable"}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            {!(opp.intakes || []).some(intakeIsAvailable) && (
              <p className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm font-semibold text-amber-700">No intake is currently open for applications.</p>
            )}
            <button data-testid="start-application-button" onClick={startApplication} disabled={!selectedIntakeId || startingApplication}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50">
              {startingApplication ? "Starting application…" : "Continue to application"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Modal>
      )}

      <Footer />
    </div>
  );
}
